import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useLookups } from "shared/hooks/use-lookups";
import { useMutation, useQuery } from "react-query";
import { client, mapServerErrors, PagedResponse } from "shared/helpers/client";
import { useAuthContext } from "shared/context/auth-context";
import { stringify } from "shared/helpers/utils";
import DataSource from "devextreme/data/data_source";
import useModal from "shared/hooks/useModel";

export enum AddressTypes {
  Company = 1,
  Customer = 2,
}


export interface AddressResponse {
  addressName: string
  addressTypeId: number
  apartmentNumber: number
  buildingNumber: number
  cityName: string
  contactPerson:  string
  id: string
  latitude:  string
  longitude: string
  neighborhoodName: string
  notes:string
  phone:string
  streetName: string
}

interface AddressData {
  type: AddressTypes;
  name: string;
  cityId: number;
  location?: {
    lat: number;
    lng: number;
  };
  contactPerson?: string;
  phone?: string;
  neighborhood?: string;
  street?: string;
  buildingNumber?: number;
  apartmentNumber?: number;
  fullAddress?: string;
}

export function useAddress(type: AddressTypes) {
  const { t } = useTranslation();
  const [serverErrors,setServerErrors] = React.useState({});
  const [{ user }] = useAuthContext();
   const [totalCount,setTotalCount] = React.useState(1)
  const [isAddingAddress, openNewAddressDialog, closeNewAddressDialog] = useModal(false);

  // const [filters,setFilters] = useState( () => ({
  //   addressTypeId: type ,
  //   userId:user.sub,
  //   pageNumber:0,
  //   pageSize: 1,
  // }))
  const schema = React.useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("errors.required")),
        cityId: yup.number().required(t("errors.required")),
        location: yup.object().nullable().shape({
          lat: yup.string(),
          long: yup.string(),
        }),
        contactPerson: yup.string(),
        phone: yup.string(),
        neighborhood: yup.string(),
        street: yup.string(),
        buildingNumber: yup.string().nullable(),
        apartmentNumber: yup.string().nullable(),
        fullAddress: yup.string(),
      }),
    [t]
  );
  
  
  
  const { activeCities } = useLookups(["active-cities"]);

  const { data: addresses } = useQuery(["addresses",type], () =>
    client<PagedResponse<AddressResponse>>("/customerAddresses/getCustomerAddresses", {
      params: {
        userId:user.sub,
        addressTypeId: type,
           pageNumber:0,
        pageSize: 10000,
      },
    }).then(r => r.response)
  );

  const addAddressServer = useMutation((addressDto: any) =>
    client("/customerAddresses/addAddress", { data: addressDto })
  );

  const deleteAddressServer = useMutation((addressId: string) =>
    client("/customerAddresses/deleteAddress", { params: { addressId },method:"POST" })
  );


  const addressSource = React.useMemo(
    () =>
      new DataSource({
        key: "addressName",
        load(options) {
          return client<PagedResponse<AddressResponse>>(
            "/customerAddresses/getCustomerAddresses",
            {
              params: {
                addressTypeId: type,
                userId: user.sub,
                pageNumber: options.skip,
                pageSize: options.take,
              },
            }
          ).then((r) => {
            setTotalCount(r.response?.totalCount ?? 0);
            return r.response ?? {};
          });
        },
      }),
    [type, user.sub]
  );

  React.useEffect(() => {
    addressSource.load();
  }, [addressSource]);

  const addressForm = useForm<AddressData>({
    resolver: yupResolver(schema),
  });

  const addAddress = (data: AddressData) => {
    const addressDto = {
      userId: user.sub,
      addressName: stringify( data.name),
      cityId: data.cityId,
      neighborhoodName: data.neighborhood && stringify(data.neighborhood),
      streetName: data.street && stringify(data.street),
      buildingNumber: data.buildingNumber,
      apartmentNumber: data.apartmentNumber,
      contactPerson: data.contactPerson && stringify(data.contactPerson),
      phone: data.phone,
      longitude: data.location?.lng,
      latitude: data.location?.lat,
      addressTypeId: type,
      notes: data.fullAddress,
    };

    addAddressServer.mutate(addressDto, {
      onSuccess({ response,error }) {
        if (response) {
          closeNewAddressDialog();
          addressSource.load();
        }
        if (error) {
          setServerErrors(mapServerErrors(error))
        }
      },
    });
  };

  const deleteAddress = (addressId:string) =>  {
    deleteAddressServer.mutate(addressId,{onSuccess({response}) {
      if(response) {
        addressSource.load();
      }
      }})
  }


  return {
    serverErrors,
    isAddingAddress,
    openNewAddressDialog,
    closeNewAddressDialog,
    addressSource,
    deleteAddress,
    deleteAddressServer,
    addresses,
    totalCount,
    addressForm,
    addAddress,
    activeCities,
    addAddressServer
  };
}
